import * as Sentry from "@sentry/react";
import type { RootOptions } from "react-dom/client";
import type { getRouter } from "~/router";
import {
  getExecutionEngineBaseUrl,
  getPlatformApiBaseUrl,
  getSentryDsn,
  getSentryEnvironment,
  getSentryRelease,
  getSentryTracesSampleRate,
} from "~/lib/env";
import { getRouteVisitId, subscribeToRouteVisit } from "~/lib/route-visit";
import { registerTelemetryProvider } from "~/lib/telemetry";
import {
  TELEMETRY_REDACTED_VALUE,
  redactTelemetryString,
  redactUrlForTelemetry,
  sanitizeTelemetryContextRecord,
} from "~/lib/telemetry/privacy";
import type { TelemetryProvider } from "~/lib/telemetry";
import type { UserActionContext } from "~/lib/user-actions";

type AppRouter = ReturnType<typeof getRouter>;

interface ErrorCaptureContext {
  action?: string | null;
  correlationId?: string | null;
  errorCode?: string | null;
  extra?: Record<string, unknown>;
  interactionId?: string | null;
  name: string;
  surface?: string | null;
}

interface SentryUserActionFailureOptions {
  readonly captureException?: boolean;
  readonly correlationId?: string | null;
  readonly errorCode?: string | null;
  readonly extra?: Record<string, unknown>;
}

export interface SentryUserActionObservation {
  readonly fail: (
    error: unknown,
    options?: SentryUserActionFailureOptions,
  ) => string | null;
  readonly interactionId: string;
  readonly run: <T>(callback: () => T) => T;
  readonly succeed: (extra?: Record<string, unknown>) => void;
}

export interface SentryUserIdentityContext {
  authenticated: boolean;
  authSource: string;
  canonicalUserId: string | null;
  privyUserId: string | null;
  walletAddress: string | null;
}

interface PlatformApiRequestTelemetry {
  correlationId?: string | null;
  errorMessage?: string;
  requestId?: string | null;
  routeVisitId: string;
  status?: number;
  url: string;
}

const EXPECTED_TRPC_ERROR_CODES = new Set([
  "BAD_REQUEST",
  "FORBIDDEN",
  "NOT_FOUND",
  "TOO_MANY_REQUESTS",
  "UNAUTHORIZED",
]);
const TELEMETRY_REDACTED_QUERY_STRING = TELEMETRY_REDACTED_VALUE;

let sentryInitialized = false;
let unsubscribeRouteVisit: (() => void) | null = null;
let capturedErrorObjects = new WeakSet<object>();

export function createSentryRootOptions(): RootOptions {
  const reactErrorHandler = Sentry.reactErrorHandler((error, info) => {
    // hydrateRoot overrides React's default console reporting. Keep local
    // recovery failures observable when no Sentry client is configured.
    if (!getSentryDsn())
      console.error("React root error", error, info.componentStack);
  });

  return {
    onCaughtError: reactErrorHandler,
    onRecoverableError: reactErrorHandler,
    onUncaughtError: reactErrorHandler,
  };
}

function getObjectRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

function getErrorDetails(error: unknown): {
  code?: string;
  httpStatus?: number;
} {
  const errorRecord = getObjectRecord(error);
  if (!errorRecord) {
    return {};
  }

  const shapeRecord = getObjectRecord(errorRecord.shape);
  const errorData =
    getObjectRecord(errorRecord.data) ?? getObjectRecord(shapeRecord?.data);

  const code =
    typeof errorData?.code === "string"
      ? errorData.code
      : typeof errorRecord.code === "string"
        ? errorRecord.code
        : undefined;
  const httpStatus =
    typeof errorData?.httpStatus === "number"
      ? errorData.httpStatus
      : typeof errorRecord.status === "number"
        ? errorRecord.status
        : undefined;

  return { code, httpStatus };
}

const appendOriginToTraceTargets = (
  targets: Array<string | RegExp>,
  baseUrl: string,
): void => {
  const normalized = baseUrl.trim();
  if (!normalized) {
    return;
  }
  try {
    targets.push(
      new URL(
        normalized,
        typeof window !== "undefined" ? window.location.origin : undefined,
      ).origin,
    );
  } catch {
    // Ignore invalid URLs and retain the other configured targets.
  }
};

function getTracePropagationTargets(): Array<string | RegExp> {
  const targets: Array<string | RegExp> = [/^\/v1\//];
  appendOriginToTraceTargets(targets, getPlatformApiBaseUrl());
  appendOriginToTraceTargets(targets, getExecutionEngineBaseUrl());

  return targets;
}

function applyRouteVisitToScope(routeVisitId: string) {
  Sentry.setTag("route_visit_id", routeVisitId);
  Sentry.setContext("route_visit", { id: routeVisitId });
}

function withAppCaptureScope(
  context: ErrorCaptureContext | undefined,
  callback: () => string,
): string | null {
  let eventId: string | null = null;
  Sentry.withScope((scope) => {
    scope.setTag("route_visit_id", getRouteVisitId());
    if (context?.correlationId) {
      scope.setTag("correlation_id", context.correlationId);
    }
    if (context?.interactionId) {
      scope.setTag("interaction_id", context.interactionId);
    }
    if (context?.action) {
      scope.setTag("user_action", context.action);
    }
    if (context?.surface) {
      scope.setTag("action_surface", context.surface);
    }
    if (context?.errorCode) {
      scope.setTag("error_code", context.errorCode);
    }

    if (context?.extra) {
      scope.setContext(
        context.name,
        sanitizeTelemetryContextRecord(context.extra),
      );
    }

    eventId = callback();
  });
  return eventId;
}

export function startSentryUserActionObservation(
  context: UserActionContext,
  interactionId: string,
): SentryUserActionObservation {
  const startedAt = performance.now();
  const span = sentryInitialized
    ? Sentry.startInactiveSpan({
        attributes: {
          "app.action": context.action,
          "app.action.interaction_id": interactionId,
          "app.action.surface": context.surface,
          "app.route_visit_id": getRouteVisitId(),
        },
        forceTransaction: true,
        name: context.action,
        op: "ui.action",
      })
    : null;
  let finished = false;

  const addActionBreadcrumb = (
    outcome: "failure" | "started" | "success",
    extra?: Record<string, unknown>,
  ): void => {
    if (!sentryInitialized) {
      return;
    }

    Sentry.addBreadcrumb({
      category: "user.action",
      data: {
        ...sanitizeTelemetryContextRecord(extra ?? {}),
        action: context.action,
        duration_ms:
          outcome === "started"
            ? undefined
            : Math.max(0, Math.round(performance.now() - startedAt)),
        interaction_id: interactionId,
        outcome,
        route_visit_id: getRouteVisitId(),
        surface: context.surface,
      },
      level: outcome === "failure" ? "error" : "info",
      message: context.action,
      type: "default",
    });
  };

  addActionBreadcrumb("started");

  return {
    fail(error, options = {}) {
      if (finished) {
        return null;
      }
      finished = true;
      span?.setStatus({ code: 2 });
      addActionBreadcrumb("failure", options.extra);

      const capture = (): string | null =>
        options.captureException === false
          ? null
          : captureUnexpectedAppException(error, {
              action: context.action,
              correlationId: options.correlationId,
              errorCode: options.errorCode,
              extra: {
                ...options.extra,
                durationMs: Math.max(
                  0,
                  Math.round(performance.now() - startedAt),
                ),
              },
              interactionId,
              name: "user_action",
              surface: context.surface,
            });
      const eventId = span ? Sentry.withActiveSpan(span, capture) : capture();
      span?.end();
      return eventId;
    },
    interactionId,
    run(callback) {
      return span ? Sentry.withActiveSpan(span, callback) : callback();
    },
    succeed(extra) {
      if (finished) {
        return;
      }
      finished = true;
      span?.setStatus({ code: 1 });
      addActionBreadcrumb("success", extra);
      span?.end();
    },
  };
}

function createSentryTelemetryProvider(): TelemetryProvider {
  return {
    name: "sentry",
    track(event) {
      Sentry.addBreadcrumb({
        category: "telemetry",
        data: event.properties,
        level: "info",
        message: event.name,
        type: "default",
      });
    },
  };
}

export function initSentry(router: AppRouter): boolean {
  const dsn = getSentryDsn();
  if (!dsn) {
    return false;
  }

  if (sentryInitialized) {
    return true;
  }

  Sentry.init({
    dsn,
    environment: getSentryEnvironment(),
    integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
    release: getSentryRelease(),
    sendDefaultPii: false,
    tracePropagationTargets: getTracePropagationTargets(),
    tracesSampleRate: getSentryTracesSampleRate(),
    beforeSend(event) {
      if (event.request?.url) {
        event.request.url = redactUrlForTelemetry(event.request.url);
      }

      if (event.request?.query_string) {
        event.request.query_string = TELEMETRY_REDACTED_QUERY_STRING;
      }

      event.tags = {
        ...event.tags,
        route_visit_id: getRouteVisitId(),
      };

      return event;
    },
  });

  sentryInitialized = true;
  applyRouteVisitToScope(getRouteVisitId());
  unsubscribeRouteVisit = subscribeToRouteVisit(applyRouteVisitToScope);
  registerTelemetryProvider(createSentryTelemetryProvider());

  return true;
}

export function captureAppException(
  error: unknown,
  context?: ErrorCaptureContext,
): string | null {
  if (!sentryInitialized) {
    return null;
  }

  const eventId = withAppCaptureScope(context, () =>
    Sentry.captureException(error),
  );
  if (eventId && typeof error === "object" && error !== null) {
    capturedErrorObjects.add(error);
  }
  return eventId;
}

export function shouldCaptureUnexpectedError(error: unknown): boolean {
  if (
    typeof error === "object" &&
    error !== null &&
    capturedErrorObjects.has(error)
  ) {
    return false;
  }

  const { code, httpStatus } = getErrorDetails(error);

  if (code && EXPECTED_TRPC_ERROR_CODES.has(code)) {
    return false;
  }

  if (httpStatus !== undefined) {
    return httpStatus >= 500;
  }

  return true;
}

export function captureUnexpectedAppException(
  error: unknown,
  context?: ErrorCaptureContext,
): string | null {
  if (!shouldCaptureUnexpectedError(error)) {
    return null;
  }

  return captureAppException(error, context);
}

export function recordPlatformApiResponseMetadata(
  metadata: PlatformApiRequestTelemetry,
): void {
  if (!sentryInitialized) {
    return;
  }

  Sentry.addBreadcrumb({
    category: "platform-api",
    data: {
      ...metadata,
      url: redactUrlForTelemetry(metadata.url),
    },
    level: metadata.status && metadata.status >= 500 ? "error" : "info",
    type: "http",
  });
}

export function recordPlatformApiRequestFailure(
  metadata: PlatformApiRequestTelemetry,
): void {
  if (!sentryInitialized) {
    return;
  }

  Sentry.addBreadcrumb({
    category: "platform-api",
    data: {
      ...metadata,
      errorMessage: metadata.errorMessage
        ? redactTelemetryString(metadata.errorMessage)
        : undefined,
      url: redactUrlForTelemetry(metadata.url),
    },
    level: "error",
    type: "http",
  });
}

export function buildSentryUser(
  context: SentryUserIdentityContext,
): Record<string, string> | null {
  if (!context.canonicalUserId) {
    return null;
  }

  return {
    id: context.canonicalUserId,
  };
}

export function setSentryUserContext(context: SentryUserIdentityContext): void {
  if (!sentryInitialized) {
    return;
  }

  const user = buildSentryUser(context);
  Sentry.setUser(user);
  Sentry.setContext(
    "auth_identity",
    user
      ? {
          auth_source: context.authSource,
        }
      : null,
  );
  Sentry.setTag("authenticated", String(context.authenticated));
  Sentry.setTag("auth_source", context.authSource);
}

export function resetSentryStateForTests(): void {
  unsubscribeRouteVisit?.();
  unsubscribeRouteVisit = null;
  sentryInitialized = false;
  capturedErrorObjects = new WeakSet<object>();
}
